// Custom hook for role-based data fetching and filtering
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { contentAPI, modulesAPI, usersAPI, explainerVideosAPI, analyticsAPI } from '../services/api';
import {
  filterContentByRole,
  filterModulesByRole,
  filterUsersByRole,
  filterExplainerVideosByRole,
  filterAnalyticsForUser,
  checkUserAccessToModule,
} from '../utils/dataFilters';
import {
  hasPermission,
  canPerformModuleAction,
  canUploadContent,
  canUploadExplainerVideos,
  canEnrollTrainees,
} from '../utils/rbac';
import { BASE_URL } from '../constants/config';

// Helper to get static asset URL
const getStaticUrl = (path) => {
  if (!path) return '';
  // If path is already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Prepend backend base URL for static assets
  const baseUrl = BASE_URL;
  // Ensure path starts with slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Transform backend content to frontend shape
 */
const transformContent = (content) => {
  return content.map(item => {
    const thumbnail = getStaticUrl(item.thumbnail || item.thumbnail_url || '');
    return {
      id: item.id,
      title: item.title,
      subtitle: item.subtitle || '',
      description: item.description || '',
      category: item.category,
      platform: item.platform,
      fileSize: item.fileSize || '',
      duration: item.duration || item.duration_minutes || 0,
      thumbnail: thumbnail,
      tags: item.tags || [],
      uploadDate: item.uploadDate || item.upload_date || '',
      uploadedBy: item.uploadedBy || item.uploaded_by,
      uploadedByName: item.uploadedByName || item.uploaded_by_name,
      status: item.status,
      apkVersion: item.apkVersion || item.apk_version || '',
      contentType: item.contentType || item.content_type,
      url: item.url || '',
      storageKey: item.storageKey || item.storage_key || '',
      completionCount: item.completionCount || item.completion_count || 0,
      createdAt: item.createdAt || item.created_at,
      // For compatibility with mock data shape
      moduleIds: item.moduleIds || item.module_ids || [] // Will be populated separately if needed
    };
  });
};

/**
 * Transform backend module to frontend shape
 */
const transformModule = (module) => {
  return {
    id: module.id,
    title: module.title,
    description: module.description || '',
    status: module.status,
    createdBy: module.createdBy || module.created_by,
    createdByName: module.createdByName || module.created_by_name,
    createdDate: module.createdDate || module.created_date,
    duration: module.duration || module.duration_minutes || 0,
    completionRate: Math.round(parseFloat(module.completionRate ?? module.completion_rate) || 0),
    thumbnail: getStaticUrl(module.thumbnail || module.thumbnail_url || ''),
    category: module.category || '',
    level: module.level || 'Beginner',
    contentCount: module.contentCount || module.content_count || 0,
    instructorCount: module.instructorCount || module.instructor_count || 0,
    traineeCount: module.traineeCount || module.trainee_count || 0,
    createdAt: module.createdAt || module.created_at,
    // For compatibility with mock data shape - arrays will be populated separately if needed
    contentIds: module.contentIds || module.content_ids || [],
    instructorIds: module.instructorIds || module.instructor_ids || [],
    traineeIds: module.traineeIds || module.trainee_ids || []
  };
};

/**
 * Transform backend user to frontend shape
 */
const transformUser = (user) => {
  // Handle first and last names from various field names
  const firstName = user.firstName || user.first_name || '';
  const lastName = user.lastName || user.last_name || '';
  // Compute name if not provided
  const name = user.name || (firstName && lastName ? `${firstName} ${lastName}` : user.name || '');

  // Compute initials
  let initials = user.initials || '';
  if (!initials) {
    if (firstName && lastName) {
      initials = (firstName[0] + lastName[0]).toUpperCase().slice(0, 2);
    } else if (name) {
      initials = name
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }
  }

  return {
    id: user.id,
    email: user.email,
    username: user.username || '',
    role: user.role,
    name,
    firstName,
    lastName,
    department: user.department || '',
    avatar: getStaticUrl(user.thumbnail || user.avatar || user.avatar_url || ''),
    initials,
    phone: user.phone || '',
    location: user.location || '',
    status: user.status || 'Active',
    token: user.token || '',
    joinDate: user.joinDate || user.join_date || '',
    lastActive: user.lastActive || user.last_active || '',
    progress: user.progress || 0,
    specialization: user.specialization || '',
    enrolledModules: user.enrolledModulesIds || user.enrolled_modules_ids || [],
    completedModules: user.completedModulesIds || user.completed_modules_ids || [],
    assignedModules: user.assignedModules || user.assigned_modules || [],
    permissions: user.permissions || [],
    // For compatibility with mock data shape (keeping old property names)
    enrolledModulesIds: user.enrolledModulesIds || user.enrolled_modules_ids || []
  };
};

/**
 * Transform backend explainer video to frontend shape
 */
const transformExplainerVideo = (video) => {
  const thumbnail = getStaticUrl(video.thumbnail || video.thumbnailUrl || video.thumbnail_url || '');
  const embedUrl = video.embedUrl || video.videoUrl || video.video_url || '';
  const transformed = {
    id: video.id,
    title: video.title,
    description: video.description || '',
    category: video.category,
    videoUrl: video.videoUrl || video.video_url || '',
    thumbnail: thumbnail,
    tags: video.tags || [],
    contentIds: video.contentIds || video.content_ids || [],
    uploadDate: video.uploadDate || video.upload_date || '',
    uploadedBy: video.uploadedBy || video.uploaded_by,
    uploadedByName: video.uploadedByName || video.uploaded_by_name,
    status: video.status,
    createdAt: video.createdAt || video.created_at,
    videoType: video.videoType || 'unknown',
    embedUrl: embedUrl,
    thumbnailUrl: video.thumbnailUrl || '',
  };
  return transformed;
};

/**
 * Custom hook that provides role-based data fetching and permission checks
 * @returns {Object} Role-based data and utility functions
 */
export const useRoleData = () => {
  const { currentUser } = useAppContext();
  const [content, setContent] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [explainerVideos, setExplainerVideos] = useState([]);
  // VR activity metrics from the backend event stream (analytics_events)
  const [activityStats, setActivityStats] = useState({ monthlyActivity: [], totalSessions: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch in parallel with individual error handling
      const [contentRes, modulesRes, usersRes, videosRes, overviewRes] = await Promise.all([
        contentAPI.getContent().catch(err => {
          console.error('Content API failed:', err);
          return { data: { data: [] } };
        }),
        modulesAPI.getModules().catch(err => {
          console.error('Modules API failed:', err);
          return { data: { data: [] } };
        }),
        usersAPI.getUsers().catch(err => {
          console.error('Users API failed:', err);
          return { data: { data: [] } };
        }),
        explainerVideosAPI.getVideos().catch(err => {
          console.error('Explainer Videos API failed:', err);
          return { data: { data: [] } };
        }),
        analyticsAPI.getOverview().catch(err => {
          console.error('Analytics overview API failed:', err);
          return null;
        })
      ]);

      const transformedContent = transformContent(contentRes.data.data);
      const transformedModules = modulesRes.data.data.map(transformModule);
      const transformedUsers = usersRes.data.data.map(transformUser);
      const transformedVideos = videosRes.data.data.map(transformExplainerVideo);

      setContent(transformedContent);
      setModules(transformedModules);
      setUsers(transformedUsers);
      setExplainerVideos(transformedVideos);
      const overview = overviewRes?.data?.data;
      setActivityStats({
        monthlyActivity: Array.isArray(overview?.monthlyActivity) ? overview.monthlyActivity : [],
        totalSessions: overview?.stats?.totalSessions || 0,
      });
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message || 'Failed to load data');
      // Keep existing data if any
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch data when currentUser changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Enrich users with module IDs from modules data
  const enrichedUsers = useMemo(() => {
    if (!users.length || !modules.length) return users;

    // Create maps for quick lookup
    const traineeToModules = {};
    const instructorToModules = {};

    modules.forEach(module => {
      // Add module ID to each trainee in module.traineeIds
      if (module.traineeIds && Array.isArray(module.traineeIds)) {
        module.traineeIds.forEach(traineeId => {
          if (!traineeToModules[traineeId]) traineeToModules[traineeId] = [];
          traineeToModules[traineeId].push(module.id);
        });
      }
      // Add module ID to each instructor in module.instructorIds
      if (module.instructorIds && Array.isArray(module.instructorIds)) {
        module.instructorIds.forEach(instructorId => {
          if (!instructorToModules[instructorId]) instructorToModules[instructorId] = [];
          instructorToModules[instructorId].push(module.id);
        });
      }
    });

    // Enrich users with module arrays
    return users.map(user => {
      const enriched = { ...user };
      if (user.role === 'Trainee' && traineeToModules[user.id]) {
        enriched.enrolledModules = [...traineeToModules[user.id]];
        // Compute completed modules (if we have that data)
        // For now, set completedModules as empty array (could be derived from module progress)
        if (!enriched.completedModules) enriched.completedModules = [];
      } else if (user.role === 'Instructor' && instructorToModules[user.id]) {
        enriched.assignedModules = [...instructorToModules[user.id]];
      } else {
        // Ensure arrays exist to prevent undefined errors
        if (user.role === 'Trainee') {
          enriched.enrolledModules = enriched.enrolledModules || [];
          enriched.completedModules = enriched.completedModules || [];
        } else if (user.role === 'Instructor') {
          enriched.assignedModules = enriched.assignedModules || [];
        }
      }
      return enriched;
    });
  }, [users, modules]);

  // Enrich current user with module arrays
  const enrichedCurrentUser = useMemo(() => {
    return enrichedUsers.find(u => u.id === currentUser?.id) || currentUser;
  }, [enrichedUsers, currentUser]);

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredContent = useMemo(() => {
    // Backend already filters by role, but we keep frontend filtering for compatibility
    return filterContentByRole(content, enrichedCurrentUser, modules);
  }, [content, enrichedCurrentUser, modules]);

  const filteredModules = useMemo(() => {
    return filterModulesByRole(modules, enrichedCurrentUser);
  }, [modules, enrichedCurrentUser]);

  const filteredUsers = useMemo(() => {
    return filterUsersByRole(enrichedUsers, currentUser);
  }, [enrichedUsers, currentUser]);

  const filteredExplainerVideos = useMemo(() => {
    return filterExplainerVideosByRole(explainerVideos, enrichedCurrentUser, modules);
  }, [explainerVideos, enrichedCurrentUser, modules]);

  const filteredAnalytics = useMemo(() => {
    // Create analytics data from fetched content, modules, users
    const analyticsData = {
      monthlyActivity: activityStats.monthlyActivity,
      moduleCompletion: filteredModules.map(module => ({
        name: module.title,
        rate: module.completionRate,
        enrolled: module.traineeCount
      })),
      contentCategories: [...new Set(filteredContent.map(c => c.category))].map(category => ({
        name: category,
        count: filteredContent.filter(c => c.category === category).length
      })),
      stats: {
        totalContent: filteredContent.length,
        activeModules: filteredModules.filter(m => m.status === 'Active').length,
        enrolledUsers: filteredUsers.filter(u => u.role === 'Trainee').length,
        avgCompletionRate: filteredModules.length > 0
          ? Math.round(filteredModules.reduce((sum, m) => sum + (m.completionRate || 0), 0) / filteredModules.length)
          : 0,
        totalSessions: activityStats.totalSessions,
        totalCompletions: filteredModules.reduce((sum, m) => sum + (m.traineeCount || 0), 0)
      }
    };
    return filterAnalyticsForUser(analyticsData, enrichedCurrentUser, modules, enrichedUsers);
  }, [filteredContent, filteredModules, filteredUsers, enrichedCurrentUser, modules, enrichedUsers, activityStats]);


  // Permission checks
  const canCreateModule = useMemo(() => {
    return hasPermission(currentUser, 'create_modules');
  }, [currentUser]);

  const canEditModule = (module) => {
    return canPerformModuleAction(enrichedCurrentUser, module, 'edit');
  };

  const canDeleteModule = (module) => {
    return canPerformModuleAction(enrichedCurrentUser, module, 'delete');
  };

  const canViewModule = (module) => {
    return canPerformModuleAction(enrichedCurrentUser, module, 'view');
  };

  const canUpload = useMemo(() => {
    return canUploadContent(currentUser);
  }, [currentUser]);

  const canUploadExplainerVideo = useMemo(() => {
    return canUploadExplainerVideos(currentUser);
  }, [currentUser]);

  const canEnroll = useMemo(() => {
    return canEnrollTrainees(currentUser);
  }, [currentUser]);

  const canViewAllContent = useMemo(() => {
    return hasPermission(currentUser, 'view_all_content');
  }, [currentUser]);

  const canViewEnrolledContent = useMemo(() => {
    return hasPermission(currentUser, 'view_enrolled_content');
  }, [currentUser]);

  const canViewSystemAnalytics = useMemo(() => {
    return hasPermission(currentUser, 'view_system_analytics');
  }, [currentUser]);

  const canViewModuleAnalytics = useMemo(() => {
    return hasPermission(currentUser, 'view_module_analytics');
  }, [currentUser]);

  const canViewPersonalAnalytics = useMemo(() => {
    return hasPermission(currentUser, 'view_personal_analytics');
  }, [currentUser]);

  /**
   * Check if current user has access to a specific module
   * @param {string} moduleId - ID of the module to check
   * @returns {boolean} True if user has access
   */
  const hasAccessToModule = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    if (!module) return false;
    return checkUserAccessToModule(enrichedCurrentUser, module);
  };

  /**
   * Get role-specific stats for dashboard
   * @returns {Object} Role-specific statistics
   */
  const getRoleStats = () => {
    const activeModules = filteredModules.filter(m => m.status === 'Active');
    const totalTrainees = filteredUsers.filter(u => u.role === 'Trainee').length;
    const totalInstructors = filteredUsers.filter(u => u.role === 'Instructor').length;

    // Calculate average completion rate for user's modules
    const avgCompletionRate = filteredModules.length > 0
      ? Math.round(filteredModules.reduce((sum, m) => sum + (m.completionRate || 0), 0) / filteredModules.length)
      : 0;

    return {
      totalModules: filteredModules.length,
      activeModules: activeModules.length,
      totalContent: filteredContent.length,
      enrolledUsers: totalTrainees,
      instructorCount: totalInstructors,
      avgCompletionRate,
    };
  };

  /**
   * Get modules that current user can edit
   * @returns {Array} Array of editable modules
   */
  const getEditableModules = () => {
    return filteredModules.filter(module => canEditModule(module));
  };

  /**
   * Get content that current user can view
   * @returns {Array} Array of viewable content
   */
  const getViewableContent = () => {
    return filteredContent;
  };

  return {
    // Filtered data
    filteredContent,
    filteredModules,
    allModules: modules,
    filteredUsers,
    filteredExplainerVideos,
    filteredAnalytics,

    // Permission checks
    canCreateModule,
    canEditModule,
    canDeleteModule,
    canViewModule,
    canUpload,
    canUploadExplainerVideo,
    canEnroll,
    canViewAllContent,
    canViewEnrolledContent,
    canViewSystemAnalytics,
    canViewModuleAnalytics,
    canViewPersonalAnalytics,

    // Utility functions
    hasAccessToModule,
    getRoleStats,
    getEditableModules,
    getViewableContent,

    // Current user (enriched with module arrays)
    currentUser: enrichedCurrentUser,

    // Loading and error states (for future use)
    loading,
    error,
    refreshData: fetchData,
  };
};